import { useState } from 'react';
import { api, type Categoria, type Conto, type Movimento, type RispostaMovimenti } from '../api';
import {
  Campo,
  CampoImporto,
  Caricamento,
  Dialogo,
  Errore,
  Etichetta,
  Importo,
  Paginazione,
  Scheda,
  Vuoto,
  useAlberoCategorie,
  useAvvisi,
  useCarico,
  useDebounce,
  useFiltriUrl,
} from '../componenti/base';
import { formattaData, formattaEuro, oggi } from '../formato';
import { useSessione } from '../main';

const TIPI_CONTO = [
  { v: 'BANCA', t: 'Conto bancario' },
  { v: 'CASSA', t: 'Cassa contanti' },
  { v: 'CARTA', t: 'Carta' },
  { v: 'ANTICIPO_FATTURE', t: 'Anticipo fatture / fido' },
];

export function Tesoreria() {
  const { organizzazione } = useSessione();
  const conti = useCarico(() => api.get<{ conti: Conto[]; cassaCent: number; fuoriCassaCent: number; fidoCent: number; disponibileCent: number }>(
        '/api/conti',
      ), []);
  const categorie = useCarico(() => api.get<Categoria[]>('/api/categorie'), []);
  // Nell'indirizzo, filtri e pagina: uscire dall'elenco e rientrarci non deve
  // far ricominciare da capo. Vale anche per il conto scelto, che è un filtro
  // a tutti gli effetti.
  const { filtri, imposta } = useFiltriUrl({
    contoId: '',
    cerca: '',
    da: '',
    a: '',
    senzaCategoria: '',
    pagina: '',
  });
  const contoId = filtri.contoId;
  const cerca = filtri.cerca;
  const da = filtri.da;
  const a = filtri.a;
  const senzaCategoria = filtri.senzaCategoria === 'si';
  const scarto = Number(filtri.pagina) || 0;
  const setScarto = (v: number) => imposta({ pagina: v > 0 ? String(v) : '' });
  const [nuovoConto, setNuovoConto] = useState<Conto | 'nuovo' | null>(null);
  const [nuovoMovimento, setNuovoMovimento] = useState(false);
  const cercaRitardata = useDebounce(cerca);

  const query = new URLSearchParams();
  if (contoId) query.set('contoId', contoId);
  if (cercaRitardata) query.set('cerca', cercaRitardata);
  if (senzaCategoria) query.set('senzaCategoria', 'true');
  if (da) query.set('da', da);
  if (a) query.set('a', a);
  query.set('scarto', String(scarto));

  const movimenti = useCarico(
    () => api.get<RispostaMovimenti>(`/api/movimenti?${query}`),
    [contoId, cercaRitardata, senzaCategoria, da, a, scarto],
  );
  const elenco = movimenti.dati?.movimenti ?? [];

  return (
    <div className="pagina space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Conti e movimenti</h1>
          <p className="text-sm text-inchiostro-500">
            Cassa di oggi <span className="num font-medium">{formattaEuro(conti.dati?.cassaCent ?? 0)}</span>
            {(conti.dati?.fidoCent ?? 0) > 0 && (
              <>
                {' '}
                · con il fido puoi arrivare a{' '}
                <span className="num font-medium" title="Cassa più fido affidato: quanto puoi effettivamente muovere oggi.">
                  {formattaEuro(conti.dati!.disponibileCent)}
                </span>
              </>
            )}
            {(conti.dati?.fuoriCassaCent ?? 0) !== 0 && (
              <> · anticipo fatture <span className="num">{formattaEuro(conti.dati!.fuoriCassaCent)}</span>, tenuto fuori dalla cassa</>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button className="bottone-secondario" onClick={() => setNuovoConto('nuovo')}>
            Nuovo conto
          </button>
          <button className="bottone-primario" onClick={() => setNuovoMovimento(true)}>
            Registra movimento
          </button>
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {(conti.dati?.conti ?? []).map((c) => (
          <button
            key={c.id}
            onClick={() => imposta({ contoId: contoId === c.id ? '' : c.id, pagina: '' })}
            className={`scheda p-4 text-left transition-colors ${contoId === c.id ? 'ring-2 ring-inchiostro-400' : 'hover:bg-inchiostro-50'}`}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium">{c.nome}</p>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setNuovoConto(c);
                }}
                className="cursor-pointer text-xs text-inchiostro-400 underline underline-offset-2 hover:text-inchiostro-800"
              >
                modifica
              </span>
            </div>
            <p className="mt-1 text-xl">
              <Importo cent={c.saldoCorrenteCent} tono="saldo" grassetto />
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-inchiostro-500">
              {!c.contribuisceAllaCassa && (
                <Etichetta
                  testo="fuori cassa"
                  tono="attenzione"
                  titolo="L'anticipo fatture non è liquidità: è fido utilizzato, e sommarlo alla cassa gonfierebbe il saldo."
                />
              )}
              {/* Senza collegamento bancario il saldo vale quanto è recente il file. */}
              {c.dataUltimoImport ? (
                <span>ultimo movimento {formattaData(c.dataUltimoImport, organizzazione.formatoData)}</span>
              ) : (
                <span className="text-attenzione">nessun movimento caricato</span>
              )}
            </div>
          </button>
        ))}
      </div>

      <Scheda senzaPadding>
        <div className="flex flex-wrap items-end gap-3 border-b border-inchiostro-200 p-3">
          <Campo etichetta="Cerca nella causale" larghezza="w-72">
            <input
              className="campo"
              value={cerca}
              onChange={(e) => imposta({ cerca: e.target.value, pagina: '' })}
              placeholder="Es. BONIFICO"
            />
          </Campo>
          <Campo etichetta="Dal" larghezza="w-40">
            <input
              type="date"
              className="campo"
              value={da}
              onChange={(e) => imposta({ da: e.target.value, pagina: '' })}
            />
          </Campo>
          <Campo etichetta="Al" larghezza="w-40">
            <input
              type="date"
              className="campo"
              value={a}
              onChange={(e) => imposta({ a: e.target.value, pagina: '' })}
            />
          </Campo>
          <label className="flex items-center gap-2 pb-2 text-sm">
            <input
              type="checkbox"
              checked={senzaCategoria}
              onChange={(e) => imposta({ senzaCategoria: e.target.checked ? 'si' : '', pagina: '' })}
            />
            Solo senza categoria
          </label>
          {(da || a) && (
            <button
              className="bottone-secondario"
              onClick={() => imposta({ da: '', a: '', pagina: '' })}
            >
              Tutto il periodo
            </button>
          )}
          {contoId && (
            <button
              className="bottone-secondario"
              onClick={() => imposta({ contoId: '', pagina: '' })}
            >
              Tutti i conti
            </button>
          )}
        </div>

        {movimenti.caricamento ? (
          <Caricamento />
        ) : movimenti.errore ? (
          <div className="p-4"><Errore testo={movimenti.errore} riprova={movimenti.ricarica} /></div>
        ) : elenco.length === 0 ? (
          <Vuoto titolo="Nessun movimento con questi filtri" />
        ) : (
          <div className="overflow-x-auto">
            <table className="dati">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Conto</th>
                  <th>Causale</th>
                  <th>Voce</th>
                  <th className="text-right">Importo</th>
                  <th className="text-right">Non abbinato</th>
                </tr>
              </thead>
              <tbody>
                {elenco.map((m) => (
                  <tr key={m.id} className={m.escluso ? 'opacity-60' : ''}>
                    <td className="num whitespace-nowrap">{formattaData(m.data, organizzazione.formatoData)}</td>
                    <td className="whitespace-nowrap text-xs text-inchiostro-600">{m.conto.nome}</td>
                    <td className="max-w-96 truncate" title={m.dicitura}>
                      {m.dicitura}
                      {m.escluso && (
                        <span className="ml-2">
                          <Etichetta
                            testo="escluso"
                            titolo="Escludere non è cancellare: il movimento resta e smette di pesare sul previsionale. Serve ai giroconti."
                          />
                        </span>
                      )}
                    </td>
                    <td>
                      {m.categoria ? (
                        // Una voce ereditata dalla fattura abbinata non l'ha
                        // scelta nessuno per questo movimento: si mostra più
                        // tenue, così si distingue da una decisione presa.
                        <span
                          className={m.categoriaEreditata ? 'text-inchiostro-500' : undefined}
                          title={m.categoriaEreditata ? 'Voce della fattura abbinata' : undefined}
                        >
                          {m.categoria.nome}
                          {m.categoriaEreditata && ' ·'}
                        </span>
                      ) : (
                        // Un movimento senza categoria è uno stato da sanare,
                        // non un valore neutro: si segnala.
                        <Etichetta testo="da categorizzare" tono="attenzione" />
                      )}
                    </td>
                    <td className="text-right">
                      <Importo cent={m.importoCent} tono="auto" />
                    </td>
                    <td className="text-right text-xs">
                      {m.residuoCent === 0 ? (
                        <span className="text-inchiostro-400">abbinato</span>
                      ) : (
                        <Importo cent={m.residuoCent} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {movimenti.dati && (
          <Paginazione
            scarto={movimenti.dati.scarto}
            mostrati={elenco.length}
            totale={movimenti.dati.totale}
            limite={movimenti.dati.limite}
            onPagina={setScarto}
          />
        )}
      </Scheda>

      {nuovoConto && (
        <FormConto
          conto={nuovoConto === 'nuovo' ? null : nuovoConto}
          onChiudi={() => setNuovoConto(null)}
          onSalvato={() => {
            setNuovoConto(null);
            conti.ricarica();
            movimenti.ricarica();
          }}
        />
      )}
      {nuovoMovimento && (
        <FormMovimento
          conti={conti.dati?.conti ?? []}
          categorie={categorie.dati ?? []}
          onChiudi={() => setNuovoMovimento(false)}
          onSalvato={() => {
            setNuovoMovimento(false);
            conti.ricarica();
            movimenti.ricarica();
          }}
        />
      )}
    </div>
  );
}

function FormConto({ conto, onChiudi, onSalvato }: { conto: Conto | null; onChiudi: () => void; onSalvato: () => void }) {
  const avvisa = useAvvisi();
  const [nome, setNome] = useState(conto?.nome ?? '');
  const [tipo, setTipo] = useState(conto?.tipo ?? 'BANCA');
  const [iban, setIban] = useState(conto?.iban ?? '');
  const [banca, setBanca] = useState(conto?.banca ?? '');
  const [saldoIniziale, setSaldoIniziale] = useState(conto?.saldoInizialeCent ?? 0);
  const [fido, setFido] = useState(conto?.fidoCent ?? 0);
  const [attesa, setAttesa] = useState(false);

  const salva = async (e: React.FormEvent) => {
    e.preventDefault();
    setAttesa(true);
    try {
      const corpo = { nome, tipo, iban: iban || null, banca: banca || null, saldoInizialeCent: saldoIniziale, fidoCent: fido };
      if (conto) await api.put(`/api/conti/${conto.id}`, corpo);
      else await api.post('/api/conti', corpo);
      avvisa(conto ? 'Conto aggiornato.' : 'Conto creato.');
      onSalvato();
    } catch (err) {
      avvisa(err instanceof Error ? err.message : 'Salvataggio non riuscito.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  const elimina = async () => {
    if (!conto || !confirm(`Eliminare il conto ${conto.nome}?`)) return;
    try {
      await api.del(`/api/conti/${conto.id}`);
      avvisa('Conto eliminato.');
      onSalvato();
    } catch (err) {
      avvisa(err instanceof Error ? err.message : 'Eliminazione non riuscita.', 'errore');
    }
  };

  return (
    <Dialogo aperto titolo={conto ? `Modifica ${conto.nome}` : 'Nuovo conto'} onChiudi={onChiudi}>
      <form onSubmit={salva} className="space-y-4">
        <Campo etichetta="Nome">
          <input className="campo" required value={nome} onChange={(e) => setNome(e.target.value)} />
        </Campo>
        <Campo
          etichetta="Tipo"
          aiuto="Il tipo è un dato, non un'etichetta: l'anticipo fatture resta fuori dalla cassa perché è fido utilizzato, non liquidità."
        >
          <select className="campo" value={tipo} onChange={(e) => setTipo(e.target.value as Conto['tipo'])}>
            {TIPI_CONTO.map((t) => (
              <option key={t.v} value={t.v}>
                {t.t}
              </option>
            ))}
          </select>
        </Campo>
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo etichetta="Banca">
            <input className="campo" value={banca} onChange={(e) => setBanca(e.target.value)} />
          </Campo>
          <Campo etichetta="IBAN">
            <input className="campo font-mono text-xs" value={iban} onChange={(e) => setIban(e.target.value)} />
          </Campo>
        </div>
        <Campo
          etichetta="Saldo iniziale"
          aiuto="Il saldo corrente lo calcola Mastro: saldo iniziale più tutti i movimenti caricati."
        >
          <CampoImporto valoreCent={saldoIniziale} onCambia={setSaldoIniziale} />
        </Campo>

        <Campo
          etichetta="Fido concesso dalla banca"
          aiuto="Quanto la banca ti lascia andare sotto zero su questo conto. Non è denaro tuo: è capienza. Mastro lo tiene fuori dalla cassa e lo usa per sapere fin dove puoi spingerti davvero."
        >
          <CampoImporto valoreCent={fido} onCambia={setFido} />
        </Campo>

        <div className="flex justify-between gap-2 border-t border-inchiostro-200 pt-4">
          {conto ? (
            <button type="button" className="bottone-pericolo" onClick={elimina}>
              Elimina
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button type="button" className="bottone-secondario" onClick={onChiudi}>
              Annulla
            </button>
            <button type="submit" className="bottone-primario" disabled={attesa}>
              Salva
            </button>
          </div>
        </div>
      </form>
    </Dialogo>
  );
}

function FormMovimento({
  conti,
  categorie,
  onChiudi,
  onSalvato,
}: {
  conti: Conto[];
  categorie: Categoria[];
  onChiudi: () => void;
  onSalvato: () => void;
}) {
  const avvisa = useAvvisi();
  const { piatto } = useAlberoCategorie(categorie);
  const [contoId, setContoId] = useState(conti[0]?.id ?? '');
  const [data, setData] = useState(oggi());
  const [verso, setVerso] = useState<'ENTRATA' | 'USCITA'>('USCITA');
  const [importo, setImporto] = useState(0);
  const [dicitura, setDicitura] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [attesa, setAttesa] = useState(false);

  const salva = async (e: React.FormEvent) => {
    e.preventDefault();
    if (importo === 0) {
      avvisa('Un movimento di importo zero non è un movimento.', 'errore');
      return;
    }
    setAttesa(true);
    try {
      await api.post('/api/movimenti', {
        contoId,
        data,
        importoCent: verso === 'USCITA' ? -Math.abs(importo) : Math.abs(importo),
        dicitura,
        categoriaId: categoriaId || null,
      });
      avvisa('Movimento registrato.');
      onSalvato();
    } catch (err) {
      avvisa(err instanceof Error ? err.message : 'Salvataggio non riuscito.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  const assegnabili = piatto.filter((p) => p.categoria.verso === verso && !p.categoria.sistema);

  return (
    <Dialogo aperto titolo="Registra movimento" onChiudi={onChiudi}>
      <form onSubmit={salva} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo etichetta="Conto">
            <select className="campo" required value={contoId} onChange={(e) => setContoId(e.target.value)}>
              {conti.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          </Campo>
          <Campo etichetta="Data">
            <input type="date" className="campo" required value={data} onChange={(e) => setData(e.target.value)} />
          </Campo>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo etichetta="Verso">
            <div className="flex rounded-md border border-inchiostro-300 p-0.5">
              {(['ENTRATA', 'USCITA'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => {
                    setVerso(v);
                    setCategoriaId('');
                  }}
                  className={`flex-1 rounded px-3 py-1.5 text-sm ${
                    verso === v ? (v === 'ENTRATA' ? 'bg-incassi text-white' : 'bg-uscite text-white') : 'text-inchiostro-600'
                  }`}
                >
                  {v === 'ENTRATA' ? 'Entrata' : 'Uscita'}
                </button>
              ))}
            </div>
          </Campo>
          <Campo etichetta="Importo">
            <CampoImporto valoreCent={importo} onCambia={setImporto} />
          </Campo>
        </div>
        <Campo etichetta="Causale">
          <input className="campo" required value={dicitura} onChange={(e) => setDicitura(e.target.value)} />
        </Campo>
        <Campo
          etichetta="Voce del previsionale"
          aiuto="I movimenti senza categoria sono in «Da identificare»."
        >
          <select className="campo" value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
            <option value="">Da categorizzare</option>
            {assegnabili.map(({ categoria, livello }) => (
              <option key={categoria.id} value={categoria.id}>
                {' '.repeat(livello * 3)}
                {categoria.nome}
              </option>
            ))}
          </select>
        </Campo>
        <div className="flex justify-end gap-2 border-t border-inchiostro-200 pt-4">
          <button type="button" className="bottone-secondario" onClick={onChiudi}>
            Annulla
          </button>
          <button type="submit" className="bottone-primario" disabled={attesa}>
            Registra
          </button>
        </div>
      </form>
    </Dialogo>
  );
}
